import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bookmark, X, Hash, MessageSquare, ExternalLink } from 'lucide-react';
import { listMyBookmarks, bookmarkMessage, removeBookmark } from '../../lib/db';
import MessageAttachments from './MessageAttachments';
import RichText from './RichText';

/**
 * useBookmarks() — hook that maintains the caller's bookmarks across the
 * messaging UI. Loads once, exposes a Set of message ids for fast lookup,
 * and lets callers toggle a bookmark with optimistic updates.
 */
export function useBookmarks() {
  const [rows, setRows] = useState([]);
  const ids = useMemo(() => new Set(rows.map((r) => r.message_id)), [rows]);

  const refresh = useCallback(() => {
    listMyBookmarks().then(({ data }) => {
      setRows(Array.isArray(data) ? data : []);
    });
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async (msg) => {
    if (!msg?.id) return;
    if (ids.has(msg.id)) {
      // Optimistic remove
      setRows((prev) => prev.filter((r) => r.message_id !== msg.id));
      const { error } = await removeBookmark(msg.id);
      if (error) refresh(); // roll back from server
    } else {
      // Optimistic add with a placeholder row that gets reconciled on refresh
      setRows((prev) => [
        {
          message_id:   msg.id,
          bookmarked_at: new Date().toISOString(),
          body:         msg.body,
          attachments:  msg.attachments,
          sent_at:      msg.created_at,
          channel_id:   msg.channel_id,
          channel_name: msg.channel?.name || null,
          channel_private: msg.channel?.is_private || false,
          sender_label: msg.sender
            ? [msg.sender.first_name, msg.sender.last_name].filter(Boolean).join(' ').trim() || msg.sender.email
            : null,
        },
        ...prev,
      ]);
      const { error } = await bookmarkMessage(msg.id);
      if (error) refresh();
    }
  }, [ids, refresh]);

  return { rows, ids, toggle, refresh };
}

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Slide-in drawer that lists the caller's bookmarks across all channels.
 * Click an entry to jump to that channel (deep-link via #/messages?channel=…).
 */
export default function BookmarksDrawer({ open, onClose, bookmarks }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-slate-900/30" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
      >
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bookmark size={14} className="text-amber-500" fill="currentColor" />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Saved messages</span>
            <span className="text-xs text-slate-400">({bookmarks.length})</span>
          </div>
          <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {bookmarks.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <Bookmark size={32} className="mx-auto mb-2 opacity-30" />
              <p>No saved messages yet.</p>
              <p className="text-xs mt-1 text-slate-400">
                Hover any message and click the bookmark icon to save it for later.
              </p>
            </div>
          ) : bookmarks.map((b) => {
            const Icon = b.channel_private ? MessageSquare : Hash;
            return (
              <div
                key={b.message_id}
                className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 hover:border-indigo-400 dark:hover:border-indigo-500 transition"
              >
                <div className="flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-1.5 text-slate-500 min-w-0">
                    <Icon size={11} className="flex-shrink-0" />
                    <span className="truncate">
                      {b.channel_private ? '' : '#'}{b.channel_name || '(unknown)'}
                    </span>
                    {b.sender_label && (
                      <>
                        <span>·</span>
                        <span className="truncate">{b.sender_label}</span>
                      </>
                    )}
                  </div>
                  <span className="text-slate-400 flex-shrink-0">{formatTime(b.sent_at)}</span>
                </div>
                {b.deleted_at ? (
                  <p className="mt-1 text-xs italic text-slate-400">Original message deleted</p>
                ) : (
                  <div className="mt-1 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
                    <RichText body={b.body} />
                    <MessageAttachments attachments={b.attachments} />
                  </div>
                )}
                <div className="mt-2 flex items-center justify-end">
                  {b.channel_id && (
                    <button
                      onClick={() => { window.location.hash = `#/messages?channel=${b.channel_id}`; onClose?.(); }}
                      className="inline-flex items-center gap-1 text-xs text-indigo-700 dark:text-indigo-300 hover:underline"
                    >
                      Open channel <ExternalLink size={10} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
